import { useState } from "react";
import { updateProfile, type ProfileUpdate } from "../api.js";
import { AcademicFields, type AcademicDraft } from "./AcademicFields.js";

interface Props {
  onSaved: (update: ProfileUpdate) => void;
  onSkip: () => void;
}

/**
 * Un paso único justo después de registrarse. Sale una sola vez y no vuelve:
 * si lo saltas, el perfil se llena desde `⋯ → Mi perfil` cuando quieras.
 *
 * Los campos van acá y no dentro del registro porque alargar el registro de un
 * juego es la forma más rápida de perder a quien acaba de llegar.
 */
export function WelcomeProfile({ onSaved, onSkip }: Props) {
  const [academic, setAcademic] = useState<AcademicDraft>({
    faculty: "",
    program: "",
    semester: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);

    try {
      onSaved(
        await updateProfile({
          faculty: academic.faculty || null,
          program: academic.program || null,
          semester: academic.semester ? Number(academic.semester) : null,
        }),
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo guardar.");
    } finally {
      setBusy(false);
    }
  };

  const empty = academic.faculty === "" && academic.semester === "";

  return (
    <div className="overlay">
      <div className="panel sheet">
        <div className="sheet__head">
          <h2 className="sheet__title">¿De dónde eres en la ESPOL?</h2>
        </div>

        <p className="sheet__hint">
          Sale en tu ficha para que te encuentre gente de tu facultad. Es opcional.
        </p>

        {error && (
          <p className="sheet__error" role="alert">
            {error}
          </p>
        )}

        <AcademicFields value={academic} onChange={setAcademic} />

        <button
          className="btn profile__save"
          type="button"
          disabled={busy || empty}
          onClick={() => void save()}
        >
          {busy ? "…" : "Listo"}
        </button>

        {/* Saltar tiene que ser tan fácil de ver como guardar: los campos son
            opcionales de verdad, no un trámite disfrazado. */}
        <button className="profile__skip" type="button" onClick={onSkip}>
          Ahora no
        </button>
      </div>
    </div>
  );
}
